import Phaser from 'phaser';

/**
 * =============================================================================
 * LoadingBar - Barra de carga reutilizable (frame + fill recortado)
 * =============================================================================
 *
 * Patrón frame+fill: un `frame` (borde) fijo y un `fill` que se RECORTA con
 * `setCrop` a la fracción de progreso (0..1) → la barra "se llena" sin estirar
 * el arte. Sin texto. Ver docs/UI_SKINNING.md.
 *
 * Texturas (cargar en el preload de la escena):
 *  - `loading_frame` (/assets/ui/loading/loading_frame.png)
 *  - `loading_fill`  (/assets/ui/loading/loading_fill.png)
 */

export interface LoadingBarOptions {
  centerX: number;
  centerY: number;
  /** Ancho objetivo de la barra en px (el frame se escala a este ancho). */
  width: number;
  depth?: number;
  /** Texture keys (default loading_frame/loading_fill). */
  frameKey?: string;
  fillKey?: string;
  /** Offset vertical del fill respecto al centro del frame (default -2, ajuste fino del arte). */
  fillOffsetY?: number;
}

export class LoadingBar {
  private readonly scene: Phaser.Scene;
  private readonly frame: Phaser.GameObjects.Image;
  private readonly fill: Phaser.GameObjects.Image;
  private readonly fillFullWidth: number;
  private readonly fillFullHeight: number;
  private progress = 0;
  private indeterminate = false;
  private shimmerTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, opts: LoadingBarOptions) {
    this.scene = scene;
    const depth = opts.depth ?? 10002;
    const frameKey = opts.frameKey ?? 'loading_frame';
    const fillKey = opts.fillKey ?? 'loading_fill';

    this.frame = scene.add
      .image(opts.centerX, opts.centerY, frameKey)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(depth);

    // Escalar el frame al ancho objetivo (mantiene proporción).
    const scale = opts.width / Math.max(1, this.frame.width);
    this.frame.setScale(scale);

    // El fill usa la MISMA escala; el origen izquierdo (0, 0.5) para recortar por ancho.
    // Offset vertical configurable (default -2: 2px arriba del centro, ajuste del arte).
    const fillOffsetY = opts.fillOffsetY ?? -2;
    this.fill = scene.add
      .image(0, opts.centerY + fillOffsetY, fillKey)
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setScale(scale)
      .setDepth(depth + 1);

    this.fillFullWidth = this.fill.width; // ancho nativo de la textura del fill
    this.fillFullHeight = this.fill.height;

    // Posicionar el fill alineado al borde izquierdo interior del frame (centrado).
    const fillDisplayW = this.fillFullWidth * scale;
    this.fill.x = opts.centerX - fillDisplayW / 2;

    this.setProgress(0);
  }

  /** Fracción 0..1 de llenado. */
  setProgress(value: number): void {
    this.indeterminate = false;
    if (this.shimmerTween) {
      this.shimmerTween.stop();
      this.shimmerTween = null;
      this.fill.setAlpha(1);
    }
    this.progress = Phaser.Math.Clamp(value, 0, 1);
    // Recortar el fill a la fracción (crop en px nativos de la textura).
    const cropW = Math.max(0, Math.round(this.fillFullWidth * this.progress));
    this.fill.setCrop(0, 0, cropW, this.fillFullHeight);
    this.fill.setVisible(cropW > 0);
  }

  /**
   * Modo indeterminado: muestra el fill completo con un shimmer (pulso de alpha).
   * Útil cuando no hay porcentaje real (ej. login/transiciones).
   */
  setIndeterminate(on: boolean): void {
    this.indeterminate = on;
    if (on) {
      this.fill.setCrop(0, 0, this.fillFullWidth, this.fillFullHeight);
      this.fill.setVisible(true);
      if (!this.shimmerTween) {
        this.shimmerTween = this.scene.tweens.add({
          targets: this.fill,
          alpha: { from: 0.45, to: 1 },
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    } else {
      this.setProgress(this.progress);
    }
  }

  setVisible(v: boolean): void {
    this.frame.setVisible(v);
    this.fill.setVisible(v && (this.indeterminate || this.progress > 0));
  }

  /** Objetos para incluir en tweens de fade-out externos. */
  getObjects(): Phaser.GameObjects.Image[] {
    return [this.frame, this.fill];
  }

  setDepth(depth: number): void {
    this.frame.setDepth(depth);
    this.fill.setDepth(depth + 1);
  }

  destroy(): void {
    this.shimmerTween?.stop();
    this.shimmerTween = null;
    this.frame.destroy();
    this.fill.destroy();
  }
}
